import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, TbView } from '../../utils/api';
import { todayISO } from '../../utils/region';
import Pagination, { getPageSize } from '../Pagination';
import PeakToolbar, { BarAction } from '../PeakToolbar';
import HtmlViewer from './HtmlViewer';
import type { AwCtx, AwPeriod } from './ctx';
import { EntryDetailDialog } from './JournalBook';
import { OpeningNote, ReadOnlyNote, type GlOpening, type GlWindow } from './LedgerAccounts';
import { Loader, chip, chipOn, emptyLine, isoDate } from './dialog';
import { NoCell } from './listCells';
import { hasRecordWindow, useOpenRecord } from './relatedDocs';
import { SourceDocOpen, type SourceRef } from './sourceDoc';
import { baht, btn, card, dmy, errBox, inp, note, num, openBlob, pillGrey, sectionHead, table, td, th } from './ui';

/**
 * THE TRIAL BALANCE AS A SCREEN (owner 2026-11-02, "Modification — Trial Balance Module").
 *
 * The menu used to show the statutory print in a frame, opening on its cover. It now opens the table: company, title, period and currency
 * above it and nothing else, six figures per account under Assets, Liabilities, Equity, Revenue and Expenses, an account opening into
 * its months and a month into its transactions.
 *
 * ⛔ NOT ONE FIGURE IS COMPUTED HERE. Six figures, class subtotals, totals, the three ties, each month and the running balance are the
 * SERVER's (server/src/services/trialBalance.ts), and the screen, the sheet, the paper and Print read them on one filter (`api.ledgerTbQs`).
 * ⛔ THE STATUTORY งบทดลอง IS NOT THIS. Its cover, its route and its bytes stay, one menu away, and no filter here reaches it.
 *
 * ⛔ READ ONLY, for every seat. A figure changes by posting or reversing an entry, under Journal entries.
 *
 * ── The period ──
 * Year is an accounting period, the shell's own, because accounting_periods is what "fiscal year" means in this system: its dates are
 * free, so a March year end just works. Quarters are three calendar months each, counted from that period's first month and cut at its
 * ends, so a short first year has fewer of them; months are its calendar months. Custom is any two dates. ⛔ NO PERIOD OF ITS OWN (the
 * General ledger's rule, COA-B-6): the year picker drives ctx.setPeriod, so the bar above can never say another year than the figures.
 * A Year reads the entries the period holds, which is what the statutory print reads; the other three read by date.
 *
 * ── The drill-down ──
 * COA-D1: no row click. An account's code goes to its General ledger, its name or chevron opens it here; in a transaction the Journal No.
 * and the source number are the two links. An old entry's number is "—" with its doc_no beside it (owner's ruling, no backfill). Everything
 * under an account is read when it is opened and paged inside it, so a big account costs nothing until somebody asks.
 *
 * ── Not built, on purpose ──
 * The reference product's screenshots also hold a by-journal or by-document radio, a show-drafts box and closing figures styled as links. The owner's text
 * asks for none of them and says posted entries only, so the drafts box would contradict it; a by-document view is the owner's to ask for.
 */

type Mode = 'month' | 'quarter' | 'year' | 'custom';

// What the routes answer, as they answer it: every money field is INTEGER SATANG.
type Six = { openingDr: number; openingCr: number; periodDr: number; periodCr: number; closingDr: number; closingCr: number };
type TbRow = Six & { accountId: string; code: string; nameTh: string; nameEn: string; class: string; removed: boolean; lines: number };
type TbResult = {
  window: GlWindow; entity: string; currency: string;
  groups: { class: string; rows: TbRow[]; subtotal: Six }[];
  totals: Six; shownTotals: Six;
  ties: { opening: number; period: number; closing: number }; balanced: boolean;
  counts: { accounts: number; shown: number; zeroHidden: number; searchHidden: number; removed: number };
  filter: { q: string; zero: boolean }; label: string;
  opening: GlOpening;
  openingEntry: null | { docNos: string[]; lines: number; dr: number; cr: number; differs: number };
  closingEntry: null | { docNos: string[]; dr: number };
  /** A Year only: where the financial statements part from these figures (review 2026-11-02, finding 7). */
  statementsGap?: null | { accounts: number; amount: number; skipped: { start: string; end: string }[] };
  earlierOpen: number; beforeFirst: boolean; firstEntry: string;
};
type TbMonth = Six & { month: string; lines: number };
type TbLine = {
  lineId: string; entryId: string; date: string; journalNo: string; docNo: string;
  source: SourceRef; docRef: string; description: string; dr: number; cr: number; balance: number;
};

/** The headings. Revenue has a word of its own: aw_gl_cls_income reads Income, and the owner's document says Revenue. */
const CLASS_KEY: Record<string, string> = {
  asset: 'aw_gl_cls_asset', liability: 'aw_gl_cls_liability', equity: 'aw_gl_cls_equity', income: 'aw_tb_cls_income', expense: 'aw_gl_cls_expense',
};

// ── The spans a period holds ─────────────────────────────────────────────────────────────────────────────────────────────
export type TbSpan = { key: string; from: string; to: string };
const pad = (n: number) => String(n).padStart(2, '0');

/** A period's calendar months, each cut at the period's own ends. Nothing here knows a calendar year: a period's dates are free. */
export function monthsIn(p: Pick<AwPeriod, 'start_date' | 'end_date'> | null | undefined): TbSpan[] {
  const a = /^(\d{4})-(\d{2})/.exec(String(p?.start_date || '')), b = /^(\d{4})-(\d{2})/.exec(String(p?.end_date || ''));
  if (!a || !b) return [];
  const out: TbSpan[] = [];
  const last = +b[1] * 12 + (+b[2] - 1);
  // Bounded: a period's dates are typed by a person, and this builds a dropdown.
  for (let m = +a[1] * 12 + (+a[2] - 1); m <= last && out.length < 240; m++) {
    const y = Math.floor(m / 12), mm = m % 12 + 1, key = `${y}-${pad(mm)}`;
    const end = `${key}-${pad(new Date(Date.UTC(y, mm, 0)).getUTCDate())}`;
    const first = `${key}-01`;
    out.push({ key, from: first < p!.start_date ? p!.start_date : first, to: end > p!.end_date ? p!.end_date : end });
  }
  return out;
}

/** Three calendar months each, from the period's first month: Q1 of an April year is April to June. A short period has fewer. */
export function quartersIn(p: Pick<AwPeriod, 'start_date' | 'end_date'> | null | undefined): TbSpan[] {
  const ms = monthsIn(p), out: TbSpan[] = [];
  for (let i = 0; i < ms.length; i += 3) {
    out.push({ key: `Q${i / 3 + 1}`, from: ms[i].from, to: ms[Math.min(i + 2, ms.length - 1)].to });
  }
  return out;
}

/** The span today falls in, else the last one: an open period's month in progress, a closed year's last month. */
const currentSpan = (spans: TbSpan[]): TbSpan | null => {
  const today = todayISO();
  return spans.find(s => s.from <= today && today <= s.to) || spans[spans.length - 1] || null;
};

// Choosing another year re-keys this pane (the shell's rule, ctx.ts), so the reader's Month or Quarter is lost with it unless it is handed
// across. Only that one step: leave the menu and come back and it opens on the default again, the current accounting period.
let carriedMode: { periodId: string; mode: Mode } | null = null;
const modeCarriedTo = (periodId: string): Mode => (carriedMode && carriedMode.periodId === periodId ? carriedMode.mode : 'year');

const figure = (n: number) => (n ? baht(n) : '–');
const linkBtn: React.CSSProperties = {
  border: 'none', background: 'transparent', padding: 0, margin: 0, font: 'inherit', color: 'var(--accent-text)', cursor: 'pointer', textAlign: 'inherit' as any,
};

/** The six figure cells, each pair ruled off from the next. */
function SixCells({ v, strong, bg }: { v: Six; strong?: boolean; bg?: string }) {
  const cell = (k: keyof Six, first: boolean) => (
    <td key={k} data-six={k} style={{ ...num, ...(first ? { borderLeft: '1px solid var(--border)' } : {}), ...(bg ? { background: bg } : {}),
      fontWeight: strong ? 800 : undefined, color: v[k] ? undefined : 'var(--text-tertiary)' }}>{figure(v[k])}</td>
  );
  return <>{cell('openingDr', true)}{cell('openingCr', false)}{cell('periodDr', true)}{cell('periodCr', false)}{cell('closingDr', true)}{cell('closingCr', false)}</>;
}

const Chevron = ({ open }: { open: boolean }) => (
  <i className={open ? 'ti ti-chevron-down' : 'ti ti-chevron-right'} aria-hidden style={{ fontSize: 12, color: 'var(--text-tertiary)', marginRight: 4 }} />
);

/** What the rows under an account need from the screen they sit on. */
type Drill = {
  view: TbView; clientId: string; showDetails: boolean; reload: number;
  openMonths: Set<string>; toggleMonth: (key: string) => void;
  openRecord: ReturnType<typeof useOpenRecord>['openRecord'];
  openEntry: (id: string) => void;
};

/** The source document's number, and it opens that document here where there is a window for it. Everything else is SourceDocOpen's own compact
 *  answer (review 2026-11-02): a payroll run or remittance goes to its place, a statement line prints with the sentence saying why it has no window, a voided or
 *  withdrawn source with the server's reason. A typed VAT line or a depreciation run has no document, and saying so is the answer.
 *  ⛔ NEVER THE VOUCHER'S OWN NUMBER, the journal list's rule (COA-C10). A line's doc_ref is often that number (one entry read as its own source here and "—" there, and
 *  the entry's number is one cell to the left), so then it is "—". A reference that is not (a bank voucher's line naming the receipt it settles, 1,013 lines on the
 *  snapshot) is what this line says and prints as text: the journal list has no one line to read it from, and a number is never resolved into a link. */
function SourceNo({ l, d }: { l: TbLine; d: Drill }) {
  const s = l.source, no = String(s?.docNo || '').trim();
  if (no && s.openable && hasRecordWindow(s.kind)) {
    return (
      <button type="button" data-tb-open-source={s.kind} style={{ ...linkBtn, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        onClick={() => d.openRecord(s.kind, s.id, no, s.record || null, !!s.voided)}>{no}</button>
    );
  }
  if (no) return <span data-tb-source-text={s.kind}><SourceDocOpen clientId={d.clientId} s={s} label={no} places /></span>;
  return l.docRef && l.docRef !== l.docNo
    ? <span data-tb-source-text="ref" title={s?.why || undefined} style={{ fontVariantNumeric: 'tabular-nums' }}>{l.docRef}</span>
    : <span title={s?.why || undefined} style={{ color: 'var(--text-tertiary)' }}>—</span>;
}

/** One month's transactions: read when the month opens, paged inside it. The balance is the server's and does not restart on a page. */
function MonthLines({ accountId, month, d }: { accountId: string; month: string; d: Drill }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<{ rows: TbLine[]; total: number; limit: number } | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { setPage(1); }, [d.view, accountId, month]);
  useEffect(() => {
    let alive = true;
    api.ledgerTbLines(d.view, accountId, month, page, limit)
      .then((x: any) => { if (alive) { setData(x); setErr(''); } })
      .catch((x: any) => { if (alive) setErr(String(x?.message || x)); });
    return () => { alive = false; };
  }, [d.view, accountId, month, page, limit, d.reload]);
  if (err) return <div style={errBox}>{err}</div>;
  if (!data) return <Loader />;
  return (
    <div data-tb-lines={`${accountId}|${month}`} style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto', background: 'var(--bg-surface)' }}>
      <table style={table}>
        <thead>
          <tr>
            <th style={th}>{t('aw_col_date')}</th>
            <th style={th}>{t('aw_jl_journal_no')}</th>
            <th style={th}>{t('aw_gl_d_source')}</th>
            <th style={th}>{t('aw_tb_l_description')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('aw_jl_detail_dr')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('aw_jl_detail_cr')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('aw_gl_d_balance')}</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map(l => (
            <tr key={l.lineId} data-tb-line={l.lineId}>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(l.date)}</td>
              {/* (Owner 2026-11-02) An entry posted before the numbering reads "—" and keeps its entry number under it, a link too. */}
              <NoCell
                no={<button type="button" data-tb-open-entry={l.entryId} onClick={() => d.openEntry(l.entryId)} title={t('aw_jl_detail_view')} style={linkBtn}>{l.journalNo || '—'}</button>}
                sub={l.docNo ? <button type="button" data-tb-open-entry-doc={l.entryId} onClick={() => d.openEntry(l.entryId)} title={t('aw_jl_detail_view')} style={{ ...linkBtn, fontWeight: 400, color: 'inherit' }}>{l.docNo}</button> : undefined} />
              <td style={{ ...td, maxWidth: 220 }}><SourceNo l={l} d={d} /></td>
              <td style={{ ...td, minWidth: 180 }}>{l.description}</td>
              <td style={num}>{l.dr ? baht(l.dr) : ''}</td>
              <td style={num}>{l.cr ? baht(l.cr) : ''}</td>
              {/* The side, as the table above it says it: a negative here is a credit balance, in the words the rest of the page uses. */}
              <td data-tb-balance={l.balance} style={{ ...num, fontWeight: 600 }}>
                {baht(Math.abs(l.balance))}
                {!!l.balance && <span style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: 4 }}>{l.balance > 0 ? t('aw_gl_dr_short') : t('aw_gl_cr_short')}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
    </div>
  );
}

/** A month's label, in the reader's language. Gregorian in every one: the dates on this page are, and a Thai locale otherwise counts in BE. */
function monthLabel(ym: string, lang: string, withYear = true): string {
  const [y, m] = ym.split('-').map(Number);
  const loc = lang === 'zh' ? 'zh-CN' : lang === 'th' ? 'th-TH-u-ca-gregory' : 'en-GB';
  try { return new Intl.DateTimeFormat(loc, { month: 'short', ...(withYear ? { year: 'numeric' as const } : {}), timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, 1))); }
  catch { return ym; }
}

/** An open account's months. Rows of the same table, with the same six columns, so they read straight under the account's own figures. */
function AccountMonths({ accountId, d }: { accountId: string; d: Drill }) {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<{ rows: TbMonth[]; total: number; limit: number } | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { setPage(1); }, [d.view, accountId]);
  useEffect(() => {
    let alive = true;
    api.ledgerTbMonths(d.view, accountId, page, limit)
      .then((x: any) => { if (alive) { setData(x); setErr(''); } })
      .catch((x: any) => { if (alive) setErr(String(x?.message || x)); });
    return () => { alive = false; };
  }, [d.view, accountId, page, limit, d.reload]);
  if (err) return <tr><td colSpan={8} style={td}><div style={errBox}>{err}</div></td></tr>;
  if (!data) return <tr><td colSpan={8} style={td}><Loader /></td></tr>;
  return (
    <>
      {data.rows.map(m => {
        const key = `${accountId}|${m.month}`;
        const open = d.openMonths.has(key);
        const canOpen = d.showDetails && m.lines > 0;
        return (
          <React.Fragment key={m.month}>
            <tr data-tb-month={key}>
              <td className="aw-tb-code" style={td} />
              <td style={{ ...td, paddingLeft: 26, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                {canOpen
                  ? <button type="button" data-tb-month-open={key} aria-expanded={open} title={t('aw_tb_month_open')} onClick={() => d.toggleMonth(key)} style={{ ...linkBtn, color: 'inherit' }}><Chevron open={open} />{monthLabel(m.month, i18n.language)}</button>
                  : <span style={{ paddingLeft: 16 }}>{monthLabel(m.month, i18n.language)}</span>}
                {m.lines > 0 && <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginLeft: 6 }}>{t('aw_tb_month_lines', { count: m.lines })}</span>}
              </td>
              <SixCells v={m} />
            </tr>
            {canOpen && open && (
              <tr>
                <td colSpan={8} style={{ ...td, padding: '6px 9px 10px 26px', background: 'var(--c-gray-50)' }}>
                  <MonthLines accountId={accountId} month={m.month} d={d} />
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
      {data.total > (data.limit || limit) && (
        <tr><td colSpan={8} style={{ ...td, padding: 0 }}><Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} /></td></tr>
      )}
    </>
  );
}

const NOTE_BOX: React.CSSProperties = { ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' };

/** What makes a figure on the page read as it does, under the table: above it the owner asks for four things and no more. */
function Footnotes({ data }: { data: TbResult }) {
  const { t } = useTranslation();
  const line = (key: string, text: string, warn = false) => (
    <div key={key} data-tb-note={key} style={NOTE_BOX}>
      <i className={warn ? 'ti ti-alert-triangle' : 'ti ti-info-circle'} style={{ color: warn ? '#B42318' : 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
      <div style={{ ...note, ...(warn ? { color: '#B42318', fontWeight: 700 } : {}) }}>{text}</div>
    </div>
  );
  const oe = data.openingEntry, ce = data.closingEntry;
  return (
    <>
      {/* ⛔ "Differ by this amount" of a Year alone (review 2026-11-02): a Month or a Custom range has no one statutory print to differ from. */}
      {!!oe && line('open', t(data.window.basis === 'period' ? 'aw_tb_note_open' : 'aw_tb_note_open_range', { docNos: oe.docNos.join(', '), dr: baht(oe.dr), cr: baht(oe.cr) }))}
      {!!oe?.differs && line('open-differs', t('aw_tb_note_open_differs', { count: oe.differs }), true)}
      {/* ⛔ Said, not fixed (review 2026-11-02, finding 7): the statements' figures are what is filed, and neither they nor these move. */}
      {!!data.statementsGap && line('fs-gap', [
        t('aw_tb_note_fs_gap', { count: data.statementsGap.accounts, amount: baht(data.statementsGap.amount) }),
        ...(data.statementsGap.skipped.length ? [t('aw_tb_note_fs_gap_years', { count: data.statementsGap.skipped.length, years: data.statementsGap.skipped.map(y => `${dmy(y.start)} – ${dmy(y.end)}`).join(', ') })] : []),
      ].join(' '), true)}
      {!!ce && line('close', t('aw_tb_note_close', { docNos: ce.docNos.join(', ') }))}
      {!!data.earlierOpen && line('earlier-open', t('aw_tb_note_earlier_open', { count: data.earlierOpen }))}
      {data.beforeFirst && line('before-first', data.firstEntry ? t('aw_tb_note_before_first', { date: dmy(data.firstEntry) }) : t('aw_tb_note_no_entries'))}
      {!!data.counts.removed && line('removed', t('aw_tb_note_removed', { count: data.counts.removed }))}
      <OpeningNote o={data.opening} window={data.window} entrySaid />
    </>
  );
}

/** A file on paper, from the page the server wrote: nothing is laid out twice. A frame rather than a tab, so no popup blocker has a say. */
function printHtml(html: string) {
  const f = document.createElement('iframe');
  f.setAttribute('aria-hidden', 'true');
  f.setAttribute('data-tb-print', '');
  f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  f.srcdoc = html;
  f.onload = () => {
    try { f.contentWindow?.focus(); f.contentWindow?.print(); } finally { setTimeout(() => f.remove(), 60_000); }
  };
  document.body.appendChild(f);
}

export function TrialBalanceView({ ctx }: { ctx: AwCtx }) {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<Mode>(() => modeCarriedTo(ctx.periodId));
  useEffect(() => { carriedMode = null; }, []);
  const months = useMemo(() => monthsIn(ctx.period), [ctx.period]);
  const quarters = useMemo(() => quartersIn(ctx.period), [ctx.period]);
  const [monthKey, setMonthKey] = useState('');
  const [quarterKey, setQuarterKey] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  // Hidden by default, as the reference product: most of a chart has nothing on it. The foot says how many, and nothing it hides can move a total.
  const [hideZero, setHideZero] = useState(true);
  const [showDetails, setShowDetails] = useState(true);
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => new Set());
  const [openAccounts, setOpenAccounts] = useState<Set<string>>(() => new Set());
  const [openMonths, setOpenMonths] = useState<Set<string>>(() => new Set());
  const [data, setData] = useState<TbResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [showBook, setShowBook] = useState(false);
  const [entry, setEntry] = useState('');
  const { openRecord, recordWindow } = useOpenRecord(ctx);

  useEffect(() => { const h = setTimeout(() => setQd(q), 220); return () => clearTimeout(h); }, [q]);
  // Custom opens on the period's own dates, so its first request is a real one.
  useEffect(() => {
    if (mode !== 'custom' || customFrom || customTo) return;
    setCustomFrom(ctx.period?.start_date || ''); setCustomTo(ctx.period?.end_date || '');
  }, [mode, customFrom, customTo, ctx.period]);

  const month = months.find(m => m.key === monthKey) || currentSpan(months);
  const quarter = quarters.find(s => s.key === quarterKey) || currentSpan(quarters);
  const span = mode === 'month' ? month : mode === 'quarter' ? quarter
    : mode === 'custom' ? { key: 'custom', from: customFrom, to: customTo } : null;
  // ⛔ Backwards dates are stopped here, with a sentence, and never sent: the route refuses them, and a refused request pages the admins. So is a date the route
  // would not read (review 2026-11-02): a fifth year digit is accepted by a date box and answered 400, and so is a box left empty.
  const datesOk = isoDate(customFrom) && isoDate(customTo);
  const backwards = mode === 'custom' && datesOk && customFrom > customTo;
  const badDates = mode === 'custom' && !datesOk && !!(customFrom || customTo);
  const ready = mode === 'year' ? !!ctx.periodId : mode === 'custom' ? datesOk && !backwards : !!(span?.from && span?.to);

  /** ⛔ ONE OBJECT, FOUR EXITS: the list, the sheet, the paper and Print. */
  const view: TbView = useMemo(() => (
    mode === 'year'
      ? { periodId: ctx.periodId, q: qd, zero: !hideZero }
      : { clientId: ctx.clientId, from: span?.from || '', to: span?.to || '', q: qd, zero: !hideZero }
  ), [mode, ctx.periodId, ctx.clientId, span?.from, span?.to, qd, hideZero]);
  // What an account opens into is about the window alone, so a search does not read it all again.
  const scopeNow: TbView = useMemo(() => ({ periodId: view.periodId, clientId: view.clientId, from: view.from, to: view.to }),
    [view.periodId, view.clientId, view.from, view.to]);
  // ⛔ …and the window the table above it holds (review 2026-11-02). While the dates cannot be read the table stays on its last good answer, and an open account or
  // month did not: it followed the boxes, and each refused read paged the admins. They go on with the table until the dates can be read.
  const lastGood = useRef(scopeNow);
  if (ready) lastGood.current = scopeNow;
  const scope = lastGood.current;

  useEffect(() => {
    if (!ready) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    // The rows stay up while the next answer is on its way: a period change does not blank the page and lose the reader's place.
    api.ledgerTb(view)
      .then((d: TbResult) => { if (alive) { setData(d); setErr(''); } })
      .catch((x: any) => { if (alive) { setData(null); setErr(String(x?.message || x)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [view, ready, ctx.reload]);

  const toggle = (set: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) =>
    set(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const drill: Drill = {
    view: scope, clientId: ctx.clientId, showDetails, reload: ctx.reload, openMonths, toggleMonth: k => toggle(setOpenMonths, k),
    openRecord, openEntry: setEntry,
  };
  const collapseAll = () => { setOpenAccounts(new Set()); setOpenMonths(new Set()); };

  /** Every slow exit spins (the house's download-button rule) and only one runs at a time. */
  const run = (k: string, go: () => Promise<any>) => {
    if (busy) return;
    setBusy(k); setErr('');
    go().catch((x: any) => setErr(String(x?.message || x))).finally(() => setBusy(''));
  };
  // ⛔ The report's own exits ask for this window, so backwards dates stop them as they stop the list: the sentence is already on the page.
  const exit = (k: string, go: () => Promise<any>) => { if (ready) run(k, go); };
  // The file says which span it is, because two of them must not overwrite each other in a Downloads folder.
  const fileSpan = () => `${data?.window.from || ''}-${data?.window.to || ''}`;
  // The account's own ledger, for the window this table holds: the period as the shell holds it, or the dates. The table's and not the boxes' (review 2026-11-02):
  // backwards dates carried across would be refused over there.
  const toLedger = (r: TbRow) => ctx.setNav('accounting', 'general_ledger', {
    glAccount: r.accountId, ...(data?.window.basis === 'range' ? { glFrom: data.window.from, glTo: data.window.to } : {}),
  });

  const allGroupsClosed = !!data && data.groups.every(g => closedGroups.has(g.class));
  const actions: BarAction[] = [
    {
      key: 'export', label: t('aw_gl_export'), icon: 'ti ti-download', title: t('aw_tb_export_title'),
      busy: busy === 'xlsx' || busy === 'pdf' || busy === 'print',
      menu: [
        { key: 'xlsx', label: t('aw_jl_view_xlsx'), hint: t('aw_tb_export_hint'), icon: 'ti ti-file-spreadsheet', onClick: () => exit('xlsx', () => api.ledgerTbXlsx(view, fileSpan())) },
        { key: 'pdf', label: t('aw_jl_view_pdf'), hint: t('aw_tb_export_hint'), icon: 'ti ti-file-type-pdf', onClick: () => exit('pdf', () => api.ledgerTbPdf(view, fileSpan())) },
        { key: 'print', label: t('aw_tb_print'), hint: t('aw_tb_print_title'), icon: 'ti ti-printer', onClick: () => exit('print', async () => printHtml(await api.ledgerTbHtml(view))) },
      ],
    },
    {
      // ⛔ THE STATUTORY BOOK, a menu of its own in words of its own (the rule JournalBook.tsx wrote for the two exits): with its cover, for the
      // whole period, and ledgerBookUrl takes the period and nothing else, so nothing on this screen reaches it.
      key: 'statutory', label: t('aw_gl_statutory'), icon: 'ti ti-book', title: t('aw_gl_statutory_title'), busy: busy === 'book',
      menu: [
        { key: 'book', label: 'งบทดลอง (PDF)', hint: t('aw_tb_statutory_pdf_hint'), icon: 'ti ti-book', onClick: () => run('book', async () => openBlob(await api.ledgerBookUrl('trial-balance', ctx.periodId))) },
        { key: 'show', label: showBook ? t('aw_gl_statutory_hide') : t('aw_gl_statutory_show'), hint: t('aw_gl_statutory_show_hint'), icon: 'ti ti-eye', onClick: () => setShowBook(s => !s) },
      ],
    },
  ];

  const modeChip = (m: Mode, key: string, title?: string) => (
    <button key={m} type="button" data-tb-mode={m} aria-pressed={mode === m} title={title} onClick={() => setMode(m)}
      style={{ ...(mode === m ? { ...chip, ...chipOn } : chip), flex: 'none', whiteSpace: 'nowrap' }}>{t(key)}</button>
  );
  const filters = (
    <>
      {modeChip('month', 'aw_tb_mode_month')}
      {modeChip('quarter', 'aw_tb_mode_quarter', t('aw_tb_quarter_title'))}
      {modeChip('year', 'aw_tb_mode_year', t('aw_tb_year_title'))}
      {modeChip('custom', 'aw_tb_mode_custom')}
      {mode !== 'custom' && (
        <select data-tb-year value={ctx.periodId} aria-label={t('aw_tb_mode_year')} style={{ ...inp, flex: 'none' }}
          onChange={ev => { carriedMode = { periodId: ev.target.value, mode }; ctx.setPeriod(ev.target.value); }}>
          {ctx.periods.map(p => <option key={p.id} value={p.id}>{dmy(p.start_date)} – {dmy(p.end_date)}</option>)}
        </select>
      )}
      {mode === 'month' && (
        <select data-tb-month-pick value={month?.key || ''} aria-label={t('aw_tb_mode_month')} style={{ ...inp, flex: 'none' }} onChange={ev => setMonthKey(ev.target.value)}>
          {months.map(m => <option key={m.key} value={m.key}>{monthLabel(m.key, i18n.language)}</option>)}
        </select>
      )}
      {mode === 'quarter' && (
        <select data-tb-quarter-pick value={quarter?.key || ''} aria-label={t('aw_tb_mode_quarter')} style={{ ...inp, flex: 'none' }} onChange={ev => setQuarterKey(ev.target.value)}>
          {/* Named by its months, because Q1 of an April year is not January: the year beside it and the heading say the rest. */}
          {quarters.map(s => <option key={s.key} value={s.key}>{s.key} · {monthLabel(s.from.slice(0, 7), i18n.language, false)}–{monthLabel(s.to.slice(0, 7), i18n.language, false)}</option>)}
        </select>
      )}
      {mode === 'custom' && (
        <>
          <input type="date" data-tb-from value={customFrom} max="9999-12-31" onChange={ev => setCustomFrom(ev.target.value)} title={t('aw_jl_from')} aria-label={t('aw_jl_from')} style={{ ...inp, flex: 'none' }} />
          <span style={{ color: 'var(--text-tertiary)', flex: 'none' }}>–</span>
          <input type="date" data-tb-to value={customTo} max="9999-12-31" onChange={ev => setCustomTo(ev.target.value)} title={t('aw_jl_to')} aria-label={t('aw_jl_to')} style={{ ...inp, flex: 'none' }} />
        </>
      )}
      <span style={{ width: 1, height: 22, background: 'var(--border-strong)', flex: 'none' }} aria-hidden />
      <button type="button" data-tb-zero aria-pressed={hideZero} title={t('aw_tb_hide_zero_title')} onClick={() => setHideZero(z => !z)}
        style={{ ...(hideZero ? { ...chip, ...chipOn } : chip), flex: 'none', whiteSpace: 'nowrap' }}>
        <i className={hideZero ? 'ti ti-eye-off' : 'ti ti-eye'} aria-hidden /> {t('aw_tb_hide_zero')}
      </button>
      <button type="button" data-tb-details aria-pressed={showDetails} title={t('aw_tb_details_title')} onClick={() => setShowDetails(s => !s)}
        style={{ ...(showDetails ? { ...chip, ...chipOn } : chip), flex: 'none', whiteSpace: 'nowrap' }}>
        <i className={showDetails ? 'ti ti-eye' : 'ti ti-eye-off'} aria-hidden /> {t('aw_tb_details')}
      </button>
    </>
  );

  if (!ctx.periods.length) return <div style={note}>{t('aw_no_period')}</div>;

  const borderTop = '2px solid var(--border-strong)';
  const windowLine = data ? `${dmy(data.window.from)} – ${dmy(data.window.to)}` : '';
  const diffs = data ? ([['opening', 'aw_tb_tie_opening'], ['period', 'aw_tb_tie_period'], ['closing', 'aw_tb_tie_closing']] as const)
    .filter(([k]) => data.ties[k] !== 0).map(([k, key]) => t(key, { diff: baht(Math.abs(data.ties[k])) })) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
      <div style={{ ...card, padding: 0, overflow: 'visible', minWidth: 0 }}>
        <PeakToolbar filters={filters} actions={actions} />
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
          {/* The four things above the table, and no cover (the owner's section 1). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 10 }}>
            <div data-tb-head style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div data-tb-company style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{data?.entity || ctx.clientName}</div>
              <div data-tb-title style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('aw_sub_trial_balance')}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                <span data-tb-period>{t('aw_period')}: {windowLine}</span>
                {' · '}<span data-tb-currency>{t('aw_col_currency')}: {data?.currency || '—'}</span>
              </div>
            </div>
            {/* The table's own controls, beside it: the toolbar's room is for the period, the filters and the three exits. The search is one of them since the
                review of 2026-11-02: at the end of the strip, it was out of sight on a phone until the strip was scrolled. */}
            <input data-tb-search value={q} onChange={ev => setQ(ev.target.value)} placeholder={t('aw_tb_search_ph')} aria-label={t('aw_tb_search_ph')} style={{ ...inp, flex: '0 1 170px', minWidth: 0 }} />
            <button type="button" data-tb-collapse-all title={t('aw_tb_collapse_all_title')} onClick={collapseAll} style={btn}>
              <i className="ti ti-fold" aria-hidden /> {t('aw_tb_collapse_all')}
            </button>
            <button type="button" data-tb-groups onClick={() => setClosedGroups(allGroupsClosed ? new Set() : new Set((data?.groups || []).map(g => g.class)))} style={btn}>
              <i className="ti ti-list-tree" aria-hidden /> {allGroupsClosed ? t('aw_tb_expand_groups') : t('aw_tb_collapse_groups')}
            </button>
          </div>
          {backwards && <div data-tb-backwards style={errBox}>{t('aw_tb_backwards')}</div>}
          {badDates && <div data-tb-bad-dates style={errBox}>{t('aw_tb_bad_dates')}</div>}
          {err && <div style={errBox}>{err}</div>}
          {/* ⛔ The table scrolls inside this box and the page never does: on a phone it is wider than the glass, and `minWidth` is why. */}
          <div data-tb-scroll style={{ overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
            {!data && loading ? <Loader /> : !data ? null : (
              <table style={{ ...table, minWidth: 880 }} data-tb-table>
                {/* Widths held, so opening a month does not move the columns under the reader's eye. */}
                <colgroup>
                  <col style={{ width: 90 }} />
                  <col />
                  {[0, 1, 2, 3, 4, 5].map(i => <col key={i} style={{ width: 118 }} />)}
                </colgroup>
                <thead>
                  <tr>
                    <th style={th} colSpan={2}>{t('aw_gl_col_account')}</th>
                    {(['aw_tb_col_opening', 'aw_tb_col_period', 'aw_tb_col_closing'] as const).map(k => (
                      <th key={k} colSpan={2} style={{ ...th, textAlign: 'center', borderLeft: '1px solid var(--border)' }}>{t(k)}</th>
                    ))}
                  </tr>
                  <tr>
                    <th className="aw-tb-code" style={th}>{t('aw_gl_col_code')}</th>
                    <th className="aw-tb-name" style={{ ...th, minWidth: 200 }}>{t('aw_tb_col_name')}</th>
                    {[0, 1, 2].map(i => (
                      <React.Fragment key={i}>
                        <th style={{ ...th, textAlign: 'right', borderLeft: '1px solid var(--border)' }}>{t('aw_jl_detail_dr')}</th>
                        <th style={{ ...th, textAlign: 'right' }}>{t('aw_jl_detail_cr')}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                {!data.groups.length && (
                  <tbody><tr><td colSpan={8} style={emptyLine}>{data.filter.q ? t('aw_tb_none_search') : t('aw_tb_none')}</td></tr></tbody>
                )}
                {/* One <tbody> per class: the five keep their own zebra rhythm (global.css), and a class with no row is not drawn. */}
                {data.groups.map(g => {
                  const closed = closedGroups.has(g.class);
                  const label = CLASS_KEY[g.class] ? t(CLASS_KEY[g.class]) : (g.class || t('aw_tb_cls_other'));
                  return (
                    <tbody key={g.class || 'other'} data-tb-group={g.class}>
                      <tr>
                        <td colSpan={2} style={{ ...td, background: 'var(--c-gray-50)', fontWeight: 800 }}>
                          <button type="button" data-tb-group-toggle={g.class} aria-expanded={!closed} onClick={() => toggle(setClosedGroups, g.class)} style={{ ...linkBtn, color: 'inherit' }}>
                            <Chevron open={!closed} />{label}
                            <span style={{ fontWeight: 600, color: 'var(--text-tertiary)' }}>{' · '}{t('aw_gl_group_accounts', { count: g.rows.length })}</span>
                          </button>
                        </td>
                        <SixCells v={g.subtotal} strong bg="var(--c-gray-50)" />
                      </tr>
                      {!closed && g.rows.map(r => {
                        const open = openAccounts.has(r.accountId);
                        return (
                          <React.Fragment key={r.accountId}>
                            <tr data-tb-row={r.code || r.accountId}>
                              {/* ⛔ A removed account has no General ledger row to land on, and the route refuses one by name. */}
                              <td className="aw-tb-code" style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                {r.removed
                                  ? <span>{r.code || '—'}</span>
                                  : <button type="button" data-tb-gl={r.accountId} onClick={() => toLedger(r)} title={t('aw_tb_open_gl')} style={{ ...linkBtn, fontWeight: 700 }}>{r.code}</button>}
                              </td>
                              <td style={td}>
                                <button type="button" data-tb-expand={r.accountId} aria-expanded={open} title={t('aw_tb_expand')} onClick={() => toggle(setOpenAccounts, r.accountId)} style={{ ...linkBtn, color: 'inherit', textAlign: 'left' }}>
                                  <Chevron open={open} />
                                  <span style={{ fontWeight: 600 }}>{r.nameTh || r.nameEn || t('aw_tb_no_name')}</span>
                                  {!!r.nameEn && !!r.nameTh && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', paddingLeft: 16 }}>{r.nameEn}</span>}
                                </button>
                                {r.removed && <span data-tb-removed title={t('aw_tb_removed_title')} style={{ ...pillGrey, marginLeft: 6 }}>{t('aw_tb_removed')}</span>}
                              </td>
                              <SixCells v={r} />
                            </tr>
                            {open && <AccountMonths accountId={r.accountId} d={drill} />}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  );
                })}
                <tfoot>
                  {/* A search narrows the rows and not the books: the rows add up to their own line, and the tie stays about everything. */}
                  {!!data.filter.q && (
                    <tr data-tb-shown-total>
                      <td colSpan={2} style={{ ...td, fontWeight: 800, borderTop }}>{t('aw_tb_total_shown', { count: data.counts.shown })}</td>
                      <SixCells v={data.shownTotals} strong />
                    </tr>
                  )}
                  <tr data-tb-total>
                    <td colSpan={2} style={{ ...td, fontWeight: 800, borderTop }}>{data.filter.q ? t('aw_tb_total_all') : t('aw_tb_total')}</td>
                    <SixCells v={data.totals} strong />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
          {data && (data.balanced
            ? <div data-tb-balanced style={{ fontSize: 12, color: '#166534', fontWeight: 700 }}><i className="ti ti-check" aria-hidden /> {t('aw_tb_balanced')}</div>
            : (
              <div data-tb-unbalanced style={{ ...errBox, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <i className="ti ti-alert-triangle" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
                <div><strong>{t('aw_tb_unbalanced')}</strong> {diffs.join(' · ')}</div>
              </div>
            ))}
          {data && (
            <div data-tb-counts style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {t('aw_tb_counts', { shown: data.counts.shown, accounts: data.counts.accounts })}
              {data.counts.zeroHidden > 0 ? ` · ${t('aw_tb_counts_zero', { count: data.counts.zeroHidden })}` : ''}
              {data.counts.searchHidden > 0 ? ` · ${t('aw_tb_counts_search', { count: data.counts.searchHidden })}` : ''}
              {' · '}{t('aw_tb_posted_only')}
            </div>
          )}
          {data && <Footnotes data={data} />}
          <ReadOnlyNote />
        </div>
      </div>
      {/* The statutory print itself, as this menu used to open it, behind the menu now: its query is the period and `format=html`, the same two it always had. */}
      {showBook && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={sectionHead}>งบทดลอง · {t('aw_gl_statutory')}</div>
            <div style={{ flex: 1 }} />
            <button type="button" onClick={() => setShowBook(false)} style={btn}><i className="ti ti-x" aria-hidden /> {t('aw_gl_statutory_hide')}</button>
          </div>
          <div style={note}>{t('aw_tb_statutory_note')}</div>
          <HtmlViewer base="ledger-books" path="trial-balance" qs={{ periodId: ctx.periodId }} reload={ctx.reload} />
        </div>
      )}
      {/* Both windows open over this page, and neither moves the URL, so Esc is the way back. Read-only: no `dirty` flag reaches them. */}
      {entry && <EntryDetailDialog entryId={entry} onClose={() => setEntry('')} />}
      {recordWindow}
    </div>
  );
}

export default TrialBalanceView;
